/**
 * Auth-token helpers that avoid the Cognito Identity Pool throttle.
 *
 * `fetchAuthSession()` always does two legs: (1) resolve user-pool tokens, then
 * (2) exchange them at the Identity Pool for AWS credentials
 * (GetCredentialsForIdentity). The token leg is deduped by Amplify; the
 * credentials leg is NOT — on a cache miss it clears its in-memory cache before
 * awaiting the network call, so N concurrent callers each fire their own
 * GetCredentialsForIdentity. A token rotation (hasTokenChanged) or a multi-tab
 * wake bursts those calls and trips TooManyRequestsException — which rejects the
 * whole fetchAuthSession() even when the caller only wanted the ID token JWT.
 *
 * Most callers only need the JWT for an `Authorization: Bearer <idToken>`
 * header and never read `session.credentials`. `getIdToken()` serves them from
 * the user-pool token provider directly, skipping the identity-pool leg
 * entirely. Only callers that genuinely need AWS credentials (S3 SDK) should
 * fall back to a full session fetch.
 */
import { fetchAuthSession } from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";

/**
 * Cognito ID token JWT, user-pool only — does NOT touch the Identity Pool.
 *
 * Reads the cached user-pool tokens and lets Amplify refresh them on expiry
 * (that refresh is deduped internally). Use for any request that just needs the
 * bearer token; it can never trip the identity-pool credential throttle.
 */
export async function getIdToken(): Promise<string> {
  const tokens = await cognitoUserPoolsTokenProvider.getTokens();
  const jwt = tokens?.idToken?.toString();
  if (!jwt) throw new Error("No Cognito ID token available");
  return jwt;
}

const THROTTLE_RETRY_BASE_MS = 400;

/** Cognito Identity throttling — transient, distinct from an invalid session. */
export const isThrottleError = (error: unknown): boolean => {
  const e = error as { name?: string; message?: string };
  const blob = `${e?.name ?? ""} ${e?.message ?? ""}`.toLowerCase();
  return blob.includes("toomanyrequests") || blob.includes("rate exceeded");
};

/**
 * `fetchAuthSession()` for callers that need AWS credentials, with one jittered
 * retry on the identity-pool throttle. Because that leg has no in-flight dedupe,
 * a transient TooManyRequestsException is common under bursts; a single backoff
 * retry rides it out instead of failing the caller. Non-throttle errors and a
 * second throttle propagate unchanged.
 *
 * NOTE: this wrapper does NOT single-flight concurrent callers the way
 * AuthSessionProvider.configureAuthSession does — the one-shot retry absorbs a
 * single throttle, not a genuine N-way burst. Callers on hot paths that fan out
 * concurrent credential fetches should go through the provider's single-flight
 * instead. It's used here only by low-traffic tooling (prompt-executions debug /
 * offline-eval), where Amplify's own 50-min in-memory cred cache already serves
 * concurrent calls after the first, so an N-way burst isn't a realistic risk.
 */
export async function fetchAuthSessionWithRetry(options?: {
  forceRefresh?: boolean;
}): ReturnType<typeof fetchAuthSession> {
  try {
    return await fetchAuthSession(options);
  } catch (error) {
    if (!isThrottleError(error)) throw error;
    const delay =
      THROTTLE_RETRY_BASE_MS + Math.random() * THROTTLE_RETRY_BASE_MS;
    await new Promise((resolve) => setTimeout(resolve, delay));
    return fetchAuthSession(options);
  }
}
